import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setFilterId, setGlobalGroupBy } from "store/features";
import { setScreenshotPresets } from "store/features/userSettings/userSettingsSlice";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";

import { getScreenshotPreset, getUserOrgScreenshots } from "api/users";

import { EntityKind } from "models/entityKind";

import { ErrorBoundary } from "components/base";
import DashboardWrapper from "components/dashboard/DashboardWrapper";
import { DashboardProvider } from "components/dashboard/contexts/DashboardContext";
import { useGetCategoryList } from "components/groupBy/hooks/useGetCategoryList";
import { IconSpinner } from "components/icons";
import SingleComponentMultiphaseChart from "components/multiphase-chart/components/chart/SingleComponentMultiphaseChart";
import { ScreenshotProvider } from "components/screenshot/contexts/ScreenshotContext";
import { ScreenshotPresetProvider } from "components/screenshot/contexts/ScreenshotPresetContext";
import { SessionInfo } from "components/user";
import { useUserContext } from "components/user/context";
import { updateSession } from "components/user/context/reducer/UserReducer";
import { WorkspaceProvider } from "components/workspace/contexts/WorkspaceContext";

function SingleComponentMultiphaseChartPane() {
  // context and dispatch
  const dispatch = useDispatch();
  const [, userDispatch] = useUserContext();
  const setSelectedWells = useMapStore((s) => s.setSelectedWells);

  // state from store
  const channelId = useSelector((state: RootState) => state.channel.channel);
  const categories = useSelector((state: RootState) => state.groupBy.categoryList);
  const presets = useSelector((state: RootState) => state.userSetting.screenshotPreset);
  const token = useSelector((state: RootState) => state.auth.jwtToken);
  const user = useSelector((state: RootState) => state.auth.user);

  // state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTokenSet, setIsTokenSet] = useState(false);

  // session storage data
  const channelData = sessionStorage.getItem(channelId);

  // derived state
  const initialState = channelData ? JSON.parse(channelData) : null;

  // sync global group by and filter id
  useEffect(() => {
    if (initialState?.filterId) {
      dispatch(setFilterId(initialState.filterId));
    }

    if (initialState?.selectedWells) {
      setSelectedWells(initialState.selectedWells);
    }

    if (initialState?.globalGroupBy) {
      if (!isInitialized) {
        dispatch(setGlobalGroupBy(initialState.globalGroupBy));
      }
      setIsInitialized(true);
    }

    if (initialState?.sessionId) {
      updateSession(userDispatch, initialState.sessionId);
    }
  }, [channelId, dispatch, initialState, isInitialized]);

  // fetch and update presets, if not present
  useEffect(() => {
    if (!user) return;
    if (presets.length) return;

    const userPresets = getScreenshotPreset();
    const orgPresets = getUserOrgScreenshots();
    Promise.all([orgPresets, userPresets])
      .then((values) => {
        dispatch(setScreenshotPresets([...values[0], ...values[1]]));
      })
      // eslint-disable-next-line no-console
      .catch(console.log);
  }, [dispatch, presets, user]);

  useEffect(() => {
    if (!isTokenSet && token) {
      setIsTokenSet(true);
    }
  }, [dispatch, isTokenSet, token]);

  // fetch categories, if not already in store
  const { getCategoryListWithUnit, isReady } = useGetCategoryList();
  useEffect(() => {
    if (!isReady) {
      return;
    }
    // only get categories/presets if token is set
    if (isTokenSet && !categories.length) {
      // only get categories/presets if token is set
      getCategoryListWithUnit(EntityKind.Well);
    }
  }, [categories, isTokenSet, dispatch, isReady]);

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div
            style={{
              display: "grid",
              justifyContent: "center",
              alignItems: "center"
            }}>
            <div>
              Loading...
              <div>
                <IconSpinner />
              </div>
            </div>
          </div>
        }>
        <WorkspaceProvider>
          <DashboardProvider>
            <DashboardWrapper>
              <ScreenshotProvider>
                <ScreenshotPresetProvider>
                  <SingleComponentMultiphaseChart />
                </ScreenshotPresetProvider>
              </ScreenshotProvider>
            </DashboardWrapper>
          </DashboardProvider>
        </WorkspaceProvider>
      </Suspense>
      <SessionInfo />
    </ErrorBoundary>
  );
}

export default SingleComponentMultiphaseChartPane;
